import { Box, Tab } from '@mui/material';
import { MprcContainer, MprcIconButtonDropdown, MprcRequestErrorReport } from 'components';
import { ButtonAdd } from 'components/buttons';
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterLine } from 'components/filter';
import { IconSort } from 'components/icons';
import { MprcChipTabs } from 'components/MprcChipTabs';
import { MprcTextFieldColor } from 'components/MprcTextFieldColor';
import { FeedMyListsOrderBy, FeedMyListsType, FeedType, PublicationType, SavePublicationResultDto } from 'generated/CoreApi_v1';
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import LibUtils from 'lib/LibUtils';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { FeedContainer, FeedManager } from '../components';
import { FeedManagerHandle } from '../components/FeedManager';
import { publicationFactory } from '../FeedHelpers';

////////////////////////////////////////////////////////////////////////////////

interface IFilter {
    searchText: string;
    type: FeedMyListsType;
    orderBy: FeedMyListsOrderBy;
}

////////////////////////////////////////////////////////////////////////////////

const defaultFilterOptions = {
    type: FeedMyListsType.All,
    searchText: "",
    orderBy: FeedMyListsOrderBy.MostRecent
};

////////////////////////////////////////////////////////////////////////////////

export const FeedMyLists: React.FC = () => {

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation("knowledge");

    const [searchText, setSearchText] = useState<string>(location.state?.filter?.searchText ?? "");
    const [loading, setLoading] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [totalItems, setTotalItems] = useState<number | undefined | null>();
    const [filterOptions, setFilterOptions] = useState<IFilter>(location.state?.filter ?? defaultFilterOptions);
    const [prevPub, setPrevPub] = useState<string | undefined>(location.state?.publicationId);
    const [viewError, setViewError] = useState<any>();

    const searchRef = useRef<HTMLInputElement>(undefined);
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const filtered = filterOptions.searchText.length > 0 && !loading;

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        setLoading(true);
        var result = await api.v1.knowledgeFeed.getFeedMyLists({
            startIndex,
            endIndex,
            startPublicationId: location.state?.publicationId ?? null,
            ...filterOptions
        });
        setTotalItems(result.data.totalRows);
        setLoading(false);
        return result.data.items;
    };

    ///
    const handleFilterChange = (filter: IFilter) => {
        if (!filter.searchText) setSearchText("");
        setFilterOptions(filter);
        setTotalItems(null);
    };

    ///
    const handleCloseListPublication = (newPub: SavePublicationResultDto | null) => {
        setDialog(null);
        if (newPub?.newPublicationId) {
            navigate(KnowledgePaths.FeedList(newPub.newPublicationId));
        }
    };

    ///
    const onAdd = () => {
        setDialog(publicationFactory(null, PublicationType.List, handleCloseListPublication));
    };

    ///
    const handleChoise = (option: FeedMyListsType) => {
        handleFilterChange({ ...filterOptions, type: option });
    };

    ///
    const resetFilters = () => {
        handleFilterChange({ ...filterOptions, searchText: "" });
        searchRef.current?.focus();
    };


    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT`S
    ////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (totalItems === null) {
            navigate(document.location.search, { replace: true, state: { filter: filterOptions } });
        }
    }, [totalItems]);

    ///
    useEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(undefined);
        }
        feedRef.current?.reset();
    }, [location.state]);

    /// Retry
    useDidUpdateEffect(() => {
        if (viewError == null) feedRef.current.reset();
    }, [viewError]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    const Header = (
        <Box>
            <MprcContainer margin="dense">
                <MprcFilterBox>
                    <MprcFilterLine>
                        <ButtonAdd size="large" color="inherit" onClick={onAdd} text={t("Feed.MyLists.AddButtonText")} />
                        <MprcChipTabs
                            value={filterOptions.type}
                            onChange={(e, value) => handleChoise(value)}
                        >
                            {LibUtils.convertEnumToArray(FeedMyListsType).map(item =>
                                <Tab
                                    key={item.key}
                                    label={t(`Feed.MyLists.${item.value}FilterText`)}
                                    value={FeedMyListsType[item.value as keyof typeof FeedMyListsType]}
                                />)}
                        </MprcChipTabs>
                    </MprcFilterLine>
                    <MprcFilterLine>
                        <MprcTextFieldColor
                            fullWidth
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            inputColor="warning"
                            underlineColor="warning"
                            inputRef={searchRef}
                            autoFocus
                            autoComplete="off"
                            onKeyDown={(e) => {
                                if (e.keyCode == 13) handleFilterChange({ ...filterOptions, searchText: searchRef.current?.value ?? "" });
                            }}
                            placeholder={t("Feed.SearchTextPlaceholder")}
                        />
                        <MprcIconButtonDropdown
                            menuItemCheck
                            color={filterOptions.orderBy != FeedMyListsOrderBy.MostRecent ? "warning" : undefined}
                            icon={<IconSort />}
                            selected={filterOptions.orderBy}
                            onChange={(orderBy) => {
                                handleFilterChange({ ...filterOptions, orderBy });
                            }}
                            items={[
                                { text: t("Feed.MyLists.OrderBy.MostRecent"), value: FeedMyListsOrderBy.MostRecent },
                                { text: t("Feed.MyLists.OrderBy.Title"), value: FeedMyListsOrderBy.Title },
                                { text: t("Feed.MyLists.OrderBy.TotalItems"), value: FeedMyListsOrderBy.TotalItems }
                            ]}
                            title={t("Feed.MyLists.OrderByButtonTooltip")}
                        />
                    </MprcFilterLine>
                </MprcFilterBox>
            </MprcContainer>
            {
                !loading && <MprcFilterCountInfo
                    text={t(filtered ? "Feed.MyLists.RecordsFoundText" : "Feed.MyLists.RecordsText", { count: totalItems ?? 0 })}
                    variant={filtered ? "bodyNotice" : (totalItems ?? 0) == 0 ? "captionHeader" : "caption"}
                    onResetFilter={filtered ? resetFilters : undefined}
                />
            }
        </Box>
    );

    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => setViewError(null)}
        />
        : (
            <>
                {dialog}
                <FeedContainer
                    header={Header}
                    feed={
                        <FeedManager
                            feedType={FeedType.MyLists}
                            onGetData={onGetData}
                            ref={feedRef}
                            useInfiniteLoading
                            previousPublicationId={prevPub}
                            onGobackTop={() => setTotalItems(null)}
                            onError={(error) => setViewError(error)}
                        />
                    }
                />
            </>
        );
};
